import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleAlert, Link as LinkIcon, MessageCircle, Pause, Play, RefreshCw, RotateCcw, Send, ShieldCheck, Smartphone, UserCheck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ManagerAssistantMessageCard from './ManagerAssistantMessageCard';

interface AssistantConfig {
  id: string;
  is_enabled: boolean;
  first_message: string;
  completed_message: string;
}

interface Lead {
  id: string;
  lead_name: string | null;
  phone: string;
  status: 'link_sent' | 'completed' | 'opted_out' | 'human';
  last_inbound_content: string | null;
  updated_at: string;
}

interface Message {
  id: string;
  sender: 'lead' | 'assistant' | 'manager';
  content: string | null;
  created_at: string;
  send_status: 'pending' | 'sent' | 'failed' | null;
  send_error: string | null;
}

interface WhatsAppInstance {
  status: string;
  instance_name: string;
}

const LINK_PLACEHOLDER = '{LINK_CADASTRO_UNIDADE}';

const STANDARD_REPLY = 'Todas as informações e dúvidas sobre nossos serviços serão esclarecidas diretamente por um de nossos vendedores, que entrará em contato com você em breve. 😊';

const STATUS_BADGES: Record<Lead['status'], { label: string; className: string }> = {
  link_sent: { label: 'Aguardando cadastro', className: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Aguardando vendedor', className: 'bg-sky-100 text-sky-800' },
  human: { label: 'Atendimento humano', className: 'bg-emerald-100 text-emerald-800' },
  opted_out: { label: 'Não quer receber', className: 'bg-slate-200 text-slate-700' },
};

function saveErrorMessage(message: string) {
  if (message.includes('assistant_message_restricted_content')) {
    return 'A mensagem não pode citar valores, números, taxas, crédito, aprovação, documentos, dados pessoais ou outros links. Ela deve apenas encaminhar o contato.';
  }
  if (message.includes('assistant_welcome_missing_link')) {
    return `A mensagem de boas-vindas precisa conter ${LINK_PLACEHOLDER}, que será trocado pelo link oficial de cadastro da unidade.`;
  }
  if (message.includes('assistant_message_invalid_length')) {
    return 'A mensagem precisa ter entre 1 e 1000 caracteres.';
  }
  return 'Não foi possível salvar a mensagem agora. Tente novamente.';
}

function relativeTime(date: string) {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(date).getTime()) / 60000));
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours}h`;
  return `há ${Math.round(hours / 24)}d`;
}

export default function ManagerWhatsAppAssistantTab({ unitId }: { unitId: string }) {
  const [config, setConfig] = useState<AssistantConfig | null>(null);
  const [instance, setInstance] = useState<WhatsAppInstance | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [registrationLink, setRegistrationLink] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, [unitId]);

  useEffect(() => {
    if (!selectedLeadId) {
      setMessages([]);
      return;
    }
    void loadMessages(selectedLeadId);
  }, [selectedLeadId]);

  async function load() {
    setLoading(true);
    const [configResult, instanceResult, leadsResult, linkResult] = await Promise.all([
      supabase.from('manager_whatsapp_assistant_config').select('*').eq('unit_id', unitId).maybeSingle(),
      supabase.from('whatsapp_instances').select('status, instance_name').eq('unit_id', unitId).eq('scope', 'unit').maybeSingle(),
      supabase.from('manager_whatsapp_leads').select('id, lead_name, phone, status, last_inbound_content, updated_at').eq('unit_id', unitId).order('updated_at', { ascending: false }).limit(100),
      supabase.rpc('manager_assistant_registration_link', { p_unit_id: unitId }),
    ]);
    const loadedLeads = (leadsResult.data ?? []) as Lead[];
    setConfig(configResult.data as AssistantConfig | null);
    setInstance(instanceResult.data as WhatsAppInstance | null);
    setLeads(loadedLeads);
    setRegistrationLink(typeof linkResult.data === 'string' ? linkResult.data : null);
    setSelectedLeadId((current) => loadedLeads.some((lead) => lead.id === current) ? current : loadedLeads[0]?.id ?? '');
    setLoading(false);
  }

  async function loadMessages(leadId: string) {
    const { data } = await supabase
      .from('manager_whatsapp_messages')
      .select('id, sender, content, created_at, send_status, send_error')
      .eq('lead_id', leadId)
      .order('created_at');
    setMessages((data ?? []) as Message[]);
  }

  async function toggleAssistant() {
    setSaving(true);
    setNotice(null);
    const nextEnabled = !(config?.is_enabled ?? false);
    const query = config
      ? supabase.from('manager_whatsapp_assistant_config').update({ is_enabled: nextEnabled }).eq('id', config.id)
      : supabase.from('manager_whatsapp_assistant_config').insert({ unit_id: unitId, is_enabled: nextEnabled });
    const { error } = await query;
    if (error) {
      setNotice('Não foi possível atualizar o atendimento agora.');
    } else {
      setNotice(nextEnabled ? 'Atendimento automático ligado.' : 'Atendimento automático pausado.');
      await load();
    }
    setSaving(false);
  }

  async function saveMessage(field: 'first_message' | 'completed_message', value: string): Promise<string | null> {
    if (!config) return 'Ligue o atendimento automático antes de editar as mensagens.';
    const { error } = await supabase.from('manager_whatsapp_assistant_config').update({ [field]: value }).eq('id', config.id);
    if (error) {
      console.error('manager assistant message save failed', error);
      return saveErrorMessage(error.message);
    }
    setConfig({ ...config, [field]: value });
    setNotice('Mensagem atualizada.');
    return null;
  }

  async function resumeAutomation(leadId: string) {
    setNotice(null);
    const { error } = await supabase
      .from('manager_whatsapp_leads')
      .update({ status: 'link_sent', updated_at: new Date().toISOString() })
      .eq('id', leadId);
    if (error) {
      console.error('resume automation failed', error);
      setNotice('Não foi possível retomar o atendimento automático agora.');
      return;
    }
    setNotice('Atendimento automático retomado para este contato.');
    await load();
  }

  if (loading) return <div className="text-sm text-slate-500">Carregando atendimento do gestor...</div>;

  const enabled = config?.is_enabled ?? false;
  const connected = instance?.status === 'connected';
  const selectedLead = leads.find((lead) => lead.id === selectedLeadId) ?? null;

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white">
        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-4">
            <div className="h-12 w-12 rounded-xl bg-emerald-600 p-3 text-white shadow-sm">
              <Smartphone className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg font-semibold text-slate-900">Central do WhatsApp do gestor</p>
              <p className="mt-1 max-w-xl text-sm leading-6 text-slate-600">
                O número oficial da unidade responde sozinho apenas com o link de cadastro e avisa que um vendedor entrará em contato. Quando alguém da equipe responder, as respostas automáticas param.
              </p>
            </div>
          </div>
          <button
            onClick={toggleAssistant}
            disabled={saving}
            className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${enabled ? 'bg-slate-700 hover:bg-slate-800' : 'bg-emerald-600 hover:bg-emerald-700'}`}
          >
            {enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {saving ? 'Salvando...' : enabled ? 'Pausar atendimento' : 'Ligar atendimento'}
          </button>
        </div>
        <div className={`border-t px-6 py-3 text-sm font-medium ${enabled ? 'border-emerald-100 bg-emerald-100/60 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
          {enabled ? 'Atendimento automático ligado.' : 'Atendimento automático pausado.'}
        </div>
      </section>

      {!connected && (
        <section className="flex flex-col gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div>
              <p className="font-semibold text-amber-950">O WhatsApp do gestor ainda não está conectado</p>
              <p className="mt-1 text-sm leading-6 text-amber-900">Enquanto a conexão estiver pendente, nenhuma mensagem recebida poderá ser respondida pelo agente.</p>
            </div>
          </div>
          <Link to="/dashboard/whatsapp-settings" className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-800">
            <Smartphone className="h-4 w-4" /> Conectar WhatsApp
          </Link>
        </section>
      )}

      {notice && <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">{notice}</div>}

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><LinkIcon className="h-4 w-4 text-emerald-600" /> Link oficial de cadastro</div>
        {registrationLink ? (
          <a href={registrationLink} target="_blank" rel="noreferrer" className="break-all text-sm text-emerald-700 underline underline-offset-2 hover:text-emerald-800">{registrationLink}</a>
        ) : (
          <p className="text-sm text-red-700">Nenhum link de cadastro configurado para esta unidade. Sem ele, as boas-vindas não serão enviadas.</p>
        )}
        <p className="mt-2 text-xs leading-5 text-slate-500">Vem da configuração de cadastro de leads da unidade e é inserido automaticamente onde estiver {LINK_PLACEHOLDER}.</p>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <ManagerAssistantMessageCard
          icon={<MessageCircle className="h-4 w-4 text-emerald-600" />}
          title="Mensagem de boas-vindas"
          description="Enviada uma única vez, no primeiro contato."
          value={config?.first_message ?? ''}
          hint={`Obrigatório manter ${LINK_PLACEHOLDER}`}
          onSave={(value) => saveMessage('first_message', value)}
        />
        <ManagerAssistantMessageCard
          icon={<UserCheck className="h-4 w-4 text-emerald-600" />}
          title="Encaminhamento ao vendedor"
          description="Enviada quando o contato avisa que já preencheu o cadastro (ex.: “já preenchi”, “pronto”)."
          value={config?.completed_message ?? ''}
          onSave={(value) => saveMessage('completed_message', value)}
        />
      </section>

      <section className="flex gap-3 rounded-xl border border-sky-100 bg-sky-50 p-5">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-sky-700" />
        <div>
          <p className="text-sm font-semibold text-sky-950">Resposta padrão para qualquer outra pergunta (fixa)</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-sky-900">{STANDARD_REPLY}</p>
          <p className="mt-2 text-xs leading-5 text-sky-800">O atendimento automático nunca informa valores, taxas, parcelas, prazos, aprovação, simulações ou condições e nunca pede documentos. Essas regras valem mesmo que as mensagens sejam editadas.</p>
        </div>
      </section>

      <section className="grid min-h-[520px] overflow-hidden rounded-2xl border border-slate-200 bg-white lg:grid-cols-[320px_1fr]">
        <div className="border-b border-slate-200 lg:border-b-0 lg:border-r">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4">
            <div>
              <h2 className="font-semibold text-slate-900">Conversas do gestor</h2>
              <p className="mt-0.5 text-xs text-slate-500">{leads.length} contato{leads.length === 1 ? '' : 's'}</p>
            </div>
            <button onClick={() => void load()} className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800"><RefreshCw className="h-4 w-4" /> Atualizar</button>
          </div>
          <div className="max-h-[420px] overflow-y-auto lg:max-h-[520px]">
            {leads.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm leading-6 text-slate-500">Os novos contatos aparecerão aqui assim que o número estiver conectado.</div>
            ) : leads.map((lead) => (
              <button key={lead.id} onClick={() => setSelectedLeadId(lead.id)} className={`w-full border-b border-slate-100 px-4 py-4 text-left transition ${lead.id === selectedLeadId ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}>
                <div className="flex items-center justify-between gap-2"><p className="truncate font-medium text-slate-900">{lead.lead_name || lead.phone}</p><span className="shrink-0 text-xs text-slate-400">{relativeTime(lead.updated_at)}</span></div>
                <p className="mt-1 truncate text-sm text-slate-500">{lead.last_inbound_content || 'Sem mensagem de texto'}</p>
                <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_BADGES[lead.status].className}`}>{STATUS_BADGES[lead.status].label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex min-h-[380px] flex-col">
          {!selectedLead ? (
            <div className="flex flex-1 flex-col items-center justify-center px-5 text-center text-sm text-slate-500"><MessageCircle className="mb-3 h-8 w-8 text-slate-300" />Selecione uma conversa para acompanhar o atendimento.</div>
          ) : (
            <>
              <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{selectedLead.lead_name || selectedLead.phone}</p>
                  <p className="mt-0.5 text-sm text-slate-500">{selectedLead.phone} · {STATUS_BADGES[selectedLead.status].label}</p>
                </div>
                {selectedLead.status === 'human' && (
                  <button onClick={() => void resumeAutomation(selectedLead.id)} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50">
                    <RotateCcw className="h-4 w-4" /> Retomar atendimento automático
                  </button>
                )}
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-5">
                {messages.length === 0 ? <p className="py-12 text-center text-sm text-slate-500">Ainda não há mensagens registradas.</p> : messages.map((message) => <MessageBubble key={message.id} message={message} />)}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const outgoing = message.sender !== 'lead';
  const label = message.sender === 'assistant' ? 'Automático' : message.sender === 'manager' ? 'Equipe' : 'Contato';
  const failed = message.send_status === 'failed';
  return (
    <div className={`flex ${outgoing ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm ${outgoing ? (failed ? 'rounded-br-md bg-slate-500 text-white' : 'rounded-br-md bg-emerald-600 text-white') : 'rounded-bl-md bg-white text-slate-800'}`}>
        <div className={`mb-1 flex items-center gap-1.5 text-xs font-medium ${outgoing ? 'text-emerald-100' : 'text-slate-500'}`}><Send className="h-3 w-3" />{label}</div>
        <p className="whitespace-pre-wrap leading-6">{message.content || (failed ? 'Mensagem não montada' : 'Mensagem sem texto')}</p>
        {failed && (
          <p className="mt-2 rounded-lg bg-white/90 px-2.5 py-1.5 text-xs leading-5 text-red-700">
            Não enviada{message.send_error ? `: ${message.send_error}` : ''}
          </p>
        )}
        <p className={`mt-1 text-right text-[11px] ${outgoing ? 'text-emerald-100' : 'text-slate-400'}`}>{new Date(message.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</p>
      </div>
    </div>
  );
}
